import { newId } from "../model/surfDeck.js";
import { updateSlideInDeck, nextLayerZ } from "../editor/deckEditor.js";
import { ensureDeckContext } from "./deckContextService.js";
import { deriveSlideTextSubject, imageRoleForSlide, } from "../outline/outlineSlide.js";
import { generateSlideVisualBrief, setSlideVisualBrief } from "./visualBriefService.js";
import { submitSurfImageJob, submitSurfImageEditJob } from "./surfImageJob.js";
import { composeBakedIllustrationPrompt, getLayerBakedText, getLayerTextRender, normalizeBakedText, resolveBakedTextForLayer, } from "../illustration/bakedIllustration.js";
import { composeRasterArtifactPrompt, getRasterKind, hasExplicitNumbers, } from "../illustration/rasterKind.js";
import { aspectFromRect, bakedTextFromTextLayer, imageSizeForAspect, } from "../illustration/textRaster.js";
import { resolveRasterBackdropColor, solidBackdropPromptLines } from "../illustration/rasterBackdrop.js";
import { resolveSlideVisualPlan, setSlideFlatBackground, } from "../style/artDirection.js";
import { resolveSlideVisualPrompt, shouldAutoGenerateVisualWidgets } from "./composedBackgroundService.js";
import { isComposedBackgroundMode } from "../style/presets.js";
import { composeContextualImagePrompt, getLayerCutout, resolveImageLayerSubject, } from "./slideImageService.js";
import { buildEditPrompt, collectInputKeys, getImageReferenceKey, resolveImageAssetKey, } from "./slideImageEditService.js";
import { submitSpeakerScriptJob } from "../speaker/speakerScriptService.js";
const OPAQUE_IMAGE_JOB_OPTIONS = { background: "opaque" };
function surfTarget(op, deckId, target, applyMeta) {
    return { op, deckId, ...target, applyMeta };
}
export async function submitGenerateImageForLayer(cfg, deck, slideId, layerId, opts) {
    const slideIndex = deck.slides.findIndex((s) => s.id === slideId);
    if (slideIndex < 0)
        throw new Error("Slide non trovata");
    const slide = deck.slides[slideIndex];
    const layer = slide.layers.find((l) => l.id === layerId);
    if (!layer)
        throw new Error("Layer non trovato");
    if (layer.type !== "image")
        throw new Error("Il layer selezionato non è un widget immagine");
    const cutout = getLayerCutout(layer);
    const subject = resolveImageLayerSubject(slide, opts);
    const textRender = getLayerTextRender(layer);
    const backdrop = resolveRasterBackdropColor(layer, slide, deck.theme);
    let prompt;
    let feature = "surf.slide.background";
    let bakedUsed = getLayerBakedText(layer);
    let jobOptions;
    if (textRender === "in_image") {
        bakedUsed = resolveBakedTextForLayer(layer, slide);
        if (!bakedUsed.title && !(bakedUsed.bullets?.length ?? 0)) {
            throw new Error("Imposta titolo o bullet da cuocere nell'illustrazione.");
        }
        prompt = composeBakedIllustrationPrompt(deck.theme, subject, bakedUsed, slideIndex, false, undefined, backdrop);
        feature = "surf.illustration.baked";
        jobOptions = OPAQUE_IMAGE_JOB_OPTIONS;
    }
    else {
        prompt = composeContextualImagePrompt(deck.theme, subject, "content", slideIndex);
        if (cutout) {
            prompt += ` ${solidBackdropPromptLines(backdrop).join(" ")}`;
            jobOptions = OPAQUE_IMAGE_JOB_OPTIONS;
        }
    }
    const persistedBaked = textRender === "in_image" ? normalizeBakedText(bakedUsed) : undefined;
    const persistedBackdrop = cutout || textRender === "in_image" ? backdrop : undefined;
    const applyMeta = {
        persistedBaked,
        persistedBackdrop,
    };
    const jobId = await submitSurfImageJob(cfg, feature, prompt, {
        ...jobOptions,
        surfTarget: surfTarget("layer.image", deck.id, { slideId, layerId }, applyMeta),
    });
    return {
        deck,
        submit: {
            jobId,
            deckId: deck.id,
            op: "layer.image",
            target: { slideId, layerId },
            label: "Immagine widget",
            applyMeta,
        },
    };
}
function firstImageLayer(slide) {
    return slide.layers.find((l) => l.type === "image");
}
function ensureImageSlot(slide) {
    if (firstImageLayer(slide))
        return slide;
    const layer = {
        id: newId(),
        type: "image",
        x: 0.55,
        y: 0.15,
        w: 0.38,
        h: 0.7,
        z: nextLayerZ(slide),
        rotation: 0,
        content: "",
        style: { slotRole: "image" },
    };
    return { ...slide, layers: [...slide.layers, layer] };
}
export function isSurfJobSubmitResult(result) {
    return !("kind" in result);
}
export async function submitGenerateSlideVisual(cfg, deck, slideIndex) {
    let nextDeck = await ensureDeckContext(cfg, deck);
    const slide = nextDeck.slides[slideIndex];
    if (!slide)
        throw new Error("Slide index invalido");
    if (isComposedBackgroundMode(nextDeck.theme)) {
        const currentSlide = nextDeck.slides[slideIndex];
        if (!currentSlide.background.prompt?.trim()) {
            const briefSubject = currentSlide.visualBrief?.subject?.trim();
            if (!briefSubject || briefSubject === "abstract concept") {
                const brief = await generateSlideVisualBrief(cfg, nextDeck, slideIndex);
                nextDeck = setSlideVisualBrief(nextDeck, currentSlide.id, brief);
            }
        }
        const slideForPrompt = nextDeck.slides[slideIndex];
        const prompt = resolveSlideVisualPrompt(nextDeck.theme, slideForPrompt, slideIndex);
        const applyMeta = { planKind: "composed-background", prompt, slideIndex };
        const jobId = await submitSurfImageJob(cfg, "surf.slide.background", prompt, {
            surfTarget: surfTarget("slide.background", nextDeck.id, { slideId: slideForPrompt.id }, applyMeta),
        });
        return {
            deck: nextDeck,
            submit: {
                jobId,
                deckId: nextDeck.id,
                op: "slide.background",
                target: { slideId: slideForPrompt.id },
                label: `Sfondo slide ${slideIndex + 1}`,
                applyMeta: { ...applyMeta, jobId },
            },
        };
    }
    if (!shouldAutoGenerateVisualWidgets(nextDeck.theme)) {
        return { kind: "no-job", deck: nextDeck };
    }
    const textSubject = deriveSlideTextSubject(slide);
    let brief = slide.visualBrief ??
        ({
            subject: slide.outlineVisual?.imageSubject?.trim() || textSubject,
            imageRole: imageRoleForSlide(slide),
            aspect: "16:9",
        });
    if (!brief.subject?.trim() || brief.subject === "abstract concept") {
        brief = { ...brief, subject: slide.outlineVisual?.imageSubject?.trim() || textSubject };
    }
    if (!slide.visualBrief && brief.subject.length < 12) {
        brief = await generateSlideVisualBrief(cfg, nextDeck, slideIndex);
    }
    nextDeck = setSlideVisualBrief(nextDeck, slide.id, brief);
    const plan = resolveSlideVisualPlan(slide, nextDeck.theme, brief);
    if (plan.kind === "flat-only") {
        return { kind: "no-job", deck: setSlideFlatBackground(nextDeck, slide.id) };
    }
    if (plan.kind === "flat-plus-spot") {
        nextDeck = setSlideFlatBackground(nextDeck, slide.id);
        const prompt = resolveSlideVisualPrompt(nextDeck.theme, slide, slideIndex, {
            subject: plan.subject,
            mode: "content",
        });
        const withSlot = ensureImageSlot(nextDeck.slides[slideIndex]);
        nextDeck = updateSlideInDeck(nextDeck, slide.id, () => withSlot);
        const applyMeta = { planKind: "flat-plus-spot", slideIndex };
        const jobId = await submitSurfImageJob(cfg, "surf.slide.background", prompt, {
            surfTarget: surfTarget("slide.background", nextDeck.id, { slideId: slide.id }, applyMeta),
        });
        return {
            deck: nextDeck,
            submit: {
                jobId,
                deckId: nextDeck.id,
                op: "slide.background",
                target: { slideId: slide.id },
                label: `Immagine contestuale slide ${slideIndex + 1}`,
                applyMeta: { ...applyMeta, jobId },
            },
        };
    }
    if (brief.imageRole === "none") {
        return { kind: "no-job", deck: nextDeck };
    }
    const prompt = resolveSlideVisualPrompt(nextDeck.theme, slide, slideIndex, {
        subject: plan.subject,
        mode: brief.imageRole === "content" ? "content" : "background",
    });
    if (brief.imageRole === "background" || plan.kind === "impact-background") {
        const applyMeta = { planKind: "background", prompt, slideIndex };
        const jobId = await submitSurfImageJob(cfg, "surf.slide.background", prompt, {
            surfTarget: surfTarget("slide.background", nextDeck.id, { slideId: slide.id }, applyMeta),
        });
        return {
            deck: nextDeck,
            submit: {
                jobId,
                deckId: nextDeck.id,
                op: "slide.background",
                target: { slideId: slide.id },
                label: `Sfondo slide ${slideIndex + 1}`,
                applyMeta: { ...applyMeta, jobId },
            },
        };
    }
    const withSlot = ensureImageSlot(nextDeck.slides[slideIndex]);
    nextDeck = updateSlideInDeck(nextDeck, slide.id, () => withSlot);
    const applyMeta = { planKind: "content-slot", slideIndex };
    const jobId = await submitSurfImageJob(cfg, "surf.slide.background", prompt, {
        surfTarget: surfTarget("slide.background", nextDeck.id, { slideId: slide.id }, applyMeta),
    });
    return {
        deck: nextDeck,
        submit: {
            jobId,
            deckId: nextDeck.id,
            op: "slide.background",
            target: { slideId: slide.id },
            label: `Immagine contestuale slide ${slideIndex + 1}`,
            applyMeta: { ...applyMeta, jobId },
        },
    };
}
export async function submitGenerateRasterForTextLayer(cfg, deck, slideId, layerId, hooks) {
    const slideIndex = deck.slides.findIndex((s) => s.id === slideId);
    if (slideIndex < 0)
        throw new Error("Slide non trovata");
    const slide = deck.slides[slideIndex];
    const layer = slide.layers.find((l) => l.id === layerId);
    if (!layer || layer.type !== "text")
        throw new Error("Layer testo non trovato");
    const baked = bakedTextFromTextLayer(layer);
    if (!baked.title && !(baked.bullets?.length ?? 0)) {
        throw new Error("Il widget testo è vuoto — inserisci contenuto prima di generare.");
    }
    const aspect = aspectFromRect(layer.w, layer.h);
    const size = imageSizeForAspect(aspect);
    const subject = resolveImageLayerSubject(slide);
    const backdrop = resolveRasterBackdropColor(layer, slide, deck.theme);
    let kind = getRasterKind(layer);
    if (kind === "chart" && !hasExplicitNumbers(layer.content ?? "")) {
        kind = "diagram";
        hooks?.onWarning?.("Nessun dato numerico nel testo — generato diagramma qualitativo.");
    }
    const customPrompt = typeof layer.style?.rasterCustomPrompt === "string"
        ? layer.style.rasterCustomPrompt
        : undefined;
    const prompt = composeRasterArtifactPrompt({
        kind,
        sourceText: layer.content ?? "",
        items: baked,
        style: deck.theme,
        backdropHex: backdrop,
        aspect,
        subject,
        customPrompt,
        slideIndex,
    });
    const applyMeta = { aspect, backdrop };
    const jobId = await submitSurfImageJob(cfg, "surf.illustration.baked", prompt, {
        ...OPAQUE_IMAGE_JOB_OPTIONS,
        size,
        surfTarget: surfTarget("slide.raster.text", deck.id, { slideId, layerId }, applyMeta),
    });
    return {
        deck,
        submit: {
            jobId,
            deckId: deck.id,
            op: "slide.raster.text",
            target: { slideId, layerId },
            label: "Illustrazione raster",
            applyMeta,
        },
    };
}
export async function submitEditSlideImage(cfg, deck, target, instructions, opts) {
    const trimmed = instructions.trim();
    if (!trimmed)
        throw new Error("Inserisci istruzioni di modifica.");
    const currentKey = resolveImageAssetKey(deck, target);
    const referenceKey = opts?.referenceKey ?? getImageReferenceKey(deck, target);
    const inputAssetKeys = collectInputKeys(currentKey, referenceKey);
    if (inputAssetKeys.length === 0) {
        throw new Error("Nessuna immagine valida da modificare — genera prima un'immagine o carica un riferimento.");
    }
    const prompt = buildEditPrompt(deck, target, trimmed);
    const applyMeta = {
        instructions: trimmed,
        prompt,
        editKind: target.kind,
    };
    const jobId = await submitSurfImageEditJob(cfg, "surf.slide.image.edit", prompt, inputAssetKeys, {
        background: "opaque",
        surfTarget: surfTarget("image.edit", deck.id, {
            slideId: target.slideId,
            layerId: "layerId" in target ? target.layerId : undefined,
            editKind: target.kind,
        }, applyMeta),
    });
    const label = target.kind === "background"
        ? "Modifica sfondo"
        : target.kind === "textRaster"
            ? "Modifica raster testo"
            : "Modifica immagine";
    return {
        deck,
        submit: {
            jobId,
            deckId: deck.id,
            op: "image.edit",
            target: {
                slideId: target.slideId,
                layerId: "layerId" in target ? target.layerId : undefined,
                editKind: target.kind,
            },
            label,
            applyMeta: { ...applyMeta, jobId },
        },
    };
}
export async function submitGenerateSpeakerScript(cfg, deck, slideId, opts) {
    const submit = await submitSpeakerScriptJob(cfg, deck, slideId, opts);
    return { deck, submit };
}
